import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { TaskStateProvider } from "./context/TaskStateContext";
import { ErrorBoundary } from "./components/ui/error-boundary";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import FreeAuditPage from "./pages/FreeAuditPage";
import AuthCallback from "./pages/AuthCallback";
import DashboardLayout from "./components/layout/DashboardLayout";
import ClientDashboard from "./pages/dashboard/ClientDashboard";
import AdminDashboard from "./pages/dashboard/AdminDashboard";
import ProjectManagerDashboard from "./pages/dashboard/ProjectManagerDashboard";
import SEODashboard from "./pages/dashboard/SEODashboard";
import ContentDashboard from "./pages/dashboard/ContentDashboard";
import TaskBoard from "./pages/dashboard/TaskBoard";
import ApprovalsPage from "./pages/dashboard/ApprovalsPage";
import ReportsPage from "./pages/dashboard/ReportsPage";
import BillingPage from "./pages/dashboard/BillingPage";
import SEOPackagesComparePage from "./pages/dashboard/SEOPackagesComparePage";
import SettingsPage from "./pages/dashboard/SettingsPage";
import NotificationsPage from "./pages/dashboard/NotificationsPage";
import OnboardingPage from "./pages/dashboard/OnboardingPage";
import OnboardingCallback from "./pages/dashboard/OnboardingCallback";
import GoogleOAuthCallback from "./pages/dashboard/GoogleOAuthCallback";
import CmsOAuthCallback from "./pages/dashboard/CmsOAuthCallback";
import OnPageSEODashboard from "./pages/dashboard/OnPageSEODashboard";
import ExecutionDashboard from "./pages/dashboard/ExecutionDashboard";
import CalendarPage from "./pages/dashboard/CalendarPage";
import AdminUsersPage from "./pages/dashboard/AdminUsersPage";
import KeywordsPage from "./pages/dashboard/KeywordsPage";
import GBPDashboardPage from "./pages/dashboard/GBPDashboardPage";
import CmsDashboardPage from "./pages/dashboard/CmsDashboardPage";
import ExecutionLogPage from "./pages/dashboard/ExecutionLogPage";
import ProjectsPage from "./pages/dashboard/ProjectsPage";
import CompetitorAnalysisPage from "./pages/dashboard/CompetitorAnalysisPage";
import BacklinksPage from "./pages/dashboard/BacklinksPage";
import ImagesPage from "./pages/dashboard/ImagesPage";
import SocialMediaPage from "./pages/dashboard/SocialMediaPage";
import BingWebmasterPage from "./pages/dashboard/BingWebmasterPage";
import LocalSEOPage from "./pages/dashboard/LocalSEOPage";
import PressReleasePage from "./pages/dashboard/PressReleasePage";
import VideoSEOPage from "./pages/dashboard/VideoSEOPage";
import SmartLinksPage from "./pages/dashboard/SmartLinksPage";
import StructuredDataPage from "./pages/dashboard/StructuredDataPage";
import ContentDistributionPage from "./pages/dashboard/ContentDistributionPage";
import OffPageSEODashboard from "./pages/dashboard/OffPageSEODashboard";
import PPTSubmissionsPage from "./pages/dashboard/PPTSubmissionsPage";
import IntegrationsPage from "./pages/dashboard/IntegrationsPage";
import GeotaggingPage from "./pages/dashboard/GeotaggingPage";
import Web20ProfilesPage from "./pages/dashboard/Web20ProfilesPage";
import MicroblogPage from "./pages/dashboard/MicroblogPage";
import SitemapPage from "./pages/dashboard/SitemapPage";
import RobotsTxtPage from "./pages/dashboard/RobotsTxtPage";
import SiteAuditPage from "./pages/dashboard/SiteAuditPage";
import CoreWebVitalsPage from "./pages/dashboard/CoreWebVitalsPage";
import ImageSharingPage from "./pages/dashboard/ImageSharingPage";
import InfographicDistributionPage from "./pages/dashboard/InfographicDistributionPage";
import PinterestPage from "./pages/dashboard/PinterestPage";
import BingPlacesPage from "./pages/dashboard/BingPlacesPage";
import ClassifiedSubmissionsPage from "./pages/dashboard/ClassifiedSubmissionsPage";
import PenaltyCheckPage from "./pages/dashboard/PenaltyCheckPage";
import SocialBookmarkingPage from "./pages/dashboard/SocialBookmarkingPage";
import SyndicationPage from "./pages/dashboard/SyndicationPage";
import IndexationPage from "./pages/dashboard/IndexationPage";
import SerpRankingPage from "./pages/dashboard/SerpRankingPage";
import DuplicateContentPage from "./pages/dashboard/DuplicateContentPage";
import PackageUsagePage from "./pages/dashboard/PackageUsagePage";
import AdminPackagesPage from "./pages/dashboard/AdminPackagesPage";
import AdminLlmUsagePage from "./pages/dashboard/AdminLlmUsagePage";
import AgencyDashboardPage from "./pages/dashboard/AgencyDashboardPage";
import SharedReportPage from "./pages/SharedReportPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import VerifyEmailSentPage from "./pages/VerifyEmailSentPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import DataDeletionPage from "./pages/DataDeletionPage";
import { useProject } from "./context/ProjectContext";
import { usePackageGates } from "./context/PackageGatesContext";
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const isOnboardingRoute = location.pathname.startsWith('/onboarding') || location.pathname.startsWith('/oauth/');
  const isBillingRoute = location.pathname.startsWith('/dashboard/billing');
  const isSettingsRoute = location.pathname === '/dashboard/settings';
  const isIntegrationsRoute = location.pathname === '/dashboard/integrations';
  const isClient = user.role === 'client';
  const hasCompletedOnboarding = user.onboarding_completed;

  if (isClient && !user.email_verified && !isSettingsRoute) {
    return <Navigate to="/verify-email-sent" replace />;
  }

  if (isClient && !hasCompletedOnboarding && !isOnboardingRoute) {
    return <Navigate to="/onboarding" replace />;
  }

  // Packageless clients who finished onboarding must pick a plan before browsing features.
  // Allow billing, settings, and the dashboard home so they aren't completely locked out.
  const isDashboardHome = location.pathname === '/dashboard';
  if (
    isClient &&
    hasCompletedOnboarding &&
    !user.package_type &&
    !isBillingRoute &&
    !isSettingsRoute &&
    !isIntegrationsRoute &&
    !isDashboardHome
  ) {
    return <Navigate to="/dashboard/billing?from=onboarding" replace />;
  }

  const isSuspended = user.subscription_status === 'suspended';
  if (isSuspended && !isBillingRoute && !isSettingsRoute && !isIntegrationsRoute) {
    return <Navigate to="/dashboard/billing" replace />;
  }
  
  return children;
};

const ROLE_ROUTES = {
  '/dashboard/admin/users':     ['admin'],
  '/dashboard/admin/llm-usage': ['admin'],
  '/dashboard/projects':      ['admin'],
  '/dashboard/agency':        ['agency', 'admin'],
  '/dashboard/execution':     ['admin', 'project_manager', 'execution_team'],
  '/dashboard/execution-log': ['admin', 'project_manager', 'execution_team'],
  '/dashboard/seo':           ['admin', 'project_manager', 'execution_team'],
  '/dashboard/onpage':        ['admin', 'project_manager', 'execution_team', 'client'],
  '/dashboard/keywords':      ['admin', 'project_manager', 'client'],
  '/dashboard/competitor':    ['admin', 'project_manager', 'client'],
  '/dashboard/backlinks':     ['admin', 'project_manager', 'client'],
  '/dashboard/images':        ['admin', 'project_manager', 'client'],
  '/dashboard/content-distribution': ['admin', 'project_manager', 'client'],
  '/dashboard/offpage-seo':           ['admin', 'project_manager', 'client'],
  '/dashboard/gbp':           ['admin', 'project_manager', 'client'],
  '/dashboard/cms':           ['admin', 'project_manager', 'execution_team', 'client'],
  '/dashboard/content':       ['admin', 'project_manager', 'client', 'content_account', 'creative_account'],
  '/dashboard/tasks':         ['admin', 'project_manager', 'content_account', 'creative_account', 'execution_team'],
  '/dashboard/approvals':     ['admin', 'project_manager', 'client', 'content_account', 'creative_account', 'execution_team'],
  '/dashboard/billing':       ['client', 'agency'],
  '/dashboard/billing/compare': ['client', 'agency'],
  '/dashboard/reports':       ['admin', 'project_manager', 'client'],
};

const RoleGuard = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) return <Navigate to="/login" replace />;

  const allowedRoles = ROLE_ROUTES[location.pathname];
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Hides a feature entirely when the active project's package tier is below the minimum.
// Must render inside DashboardLayout (needs ProjectProvider + PackageGatesProvider).
const PackageGuard = ({ feature, children }) => {
  const { selectedProject } = useProject();
  const { canAccess } = usePackageGates();

  if (!canAccess(feature, selectedProject?.package_type)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

const DashboardRouter = () => {
  const { user } = useAuth();
  
  const getDashboardByRole = () => {
    switch (user?.role) {
      case 'client':
        return <ClientDashboard />;
      case 'admin':
        return <AdminDashboard />;
      case 'project_manager':
        return <ProjectManagerDashboard />;
      case 'execution_team':
        return <Navigate to="/dashboard/execution" replace />;
      case 'content_account':
      case 'creative_account':
        return <ContentDashboard />;
      case 'agency':
        return <AgencyDashboardPage />;
      default:
        return <ClientDashboard />;
    }
  };
  
  return getDashboardByRole();
};

function AppRouter() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/free-audit" element={<FreeAuditPage />} />
      <Route path="/seo-packages" element={<Navigate to="/#seo-packages" replace />} />
      <Route path="/about" element={<Navigate to="/#about" replace />} />
      <Route path="/careers" element={<Navigate to="/#careers" replace />} />
      <Route path="/contact" element={<Navigate to="/#contact" replace />} />
      <Route path="/legal" element={<Navigate to="/#legal" replace />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-and-conditions" element={<TermsOfServicePage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/data-deletion" element={<DataDeletionPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/shared/report/:token" element={<SharedReportPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/verify-email-sent" element={<VerifyEmailSentPage />} />
      
      {/* Onboarding Routes */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />
      <Route path="/onboarding/callback" element={
        <ProtectedRoute>
          <OnboardingCallback />
        </ProtectedRoute>
      } />
      <Route path="/oauth/google/callback" element={
        <ProtectedRoute>
          <GoogleOAuthCallback />
        </ProtectedRoute>
      } />
      <Route path="/cms/callback" element={
        <ProtectedRoute>
          <CmsOAuthCallback />
        </ProtectedRoute>
      } />
      
      {/* Protected Dashboard Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardRouter />} />
        <Route path="seo" element={<RoleGuard><SEODashboard /></RoleGuard>} />
        <Route path="onpage" element={<RoleGuard><OnPageSEODashboard /></RoleGuard>} />
        <Route path="content" element={<RoleGuard><ContentDashboard /></RoleGuard>} />
        <Route path="tasks" element={<RoleGuard><TaskBoard /></RoleGuard>} />
        <Route path="approvals" element={<RoleGuard><ApprovalsPage /></RoleGuard>} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="reports" element={<RoleGuard><ReportsPage /></RoleGuard>} />
        <Route path="billing" element={<RoleGuard><BillingPage /></RoleGuard>} />
        <Route path="billing/compare" element={<RoleGuard><SEOPackagesComparePage /></RoleGuard>} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="keywords" element={<RoleGuard><KeywordsPage /></RoleGuard>} />
        <Route path="gbp" element={<RoleGuard><PackageGuard feature="google_business_page"><GBPDashboardPage /></PackageGuard></RoleGuard>} />
        <Route path="cms" element={<RoleGuard><CmsDashboardPage /></RoleGuard>} />
        <Route path="execution" element={<RoleGuard><ExecutionDashboard /></RoleGuard>} />
        <Route path="execution-log" element={<RoleGuard><ExecutionLogPage /></RoleGuard>} />
        <Route path="projects" element={<RoleGuard><ProjectsPage /></RoleGuard>} />
        <Route path="competitor" element={<RoleGuard><PackageGuard feature="competitor_analysis"><CompetitorAnalysisPage /></PackageGuard></RoleGuard>} />
        <Route path="backlinks" element={<RoleGuard><PackageGuard feature="backlink_analysis"><BacklinksPage /></PackageGuard></RoleGuard>} />
        <Route path="images" element={<RoleGuard><PackageGuard feature="infographics"><ImagesPage /></PackageGuard></RoleGuard>} />
        <Route path="social" element={<RoleGuard><SocialMediaPage /></RoleGuard>} />
        <Route path="bing-webmaster" element={<RoleGuard><PackageGuard feature="bing_webmaster"><BingWebmasterPage /></PackageGuard></RoleGuard>} />
        <Route path="local-seo" element={<RoleGuard><LocalSEOPage /></RoleGuard>} />
        <Route path="press-releases" element={<RoleGuard><PackageGuard feature="press_releases"><PressReleasePage /></PackageGuard></RoleGuard>} />
        <Route path="video-seo" element={<RoleGuard><VideoSEOPage /></RoleGuard>} />
        <Route path="smart-links" element={<RoleGuard><SmartLinksPage /></RoleGuard>} />
        <Route path="structured-data" element={<RoleGuard><PackageGuard feature="structured_data"><StructuredDataPage /></PackageGuard></RoleGuard>} />
        <Route path="content-distribution" element={<RoleGuard><ContentDistributionPage /></RoleGuard>} />
        <Route path="offpage-seo" element={<RoleGuard><OffPageSEODashboard /></RoleGuard>} />
        <Route path="ppt-submissions" element={<RoleGuard><PPTSubmissionsPage /></RoleGuard>} />
        <Route path="integrations" element={<RoleGuard><IntegrationsPage /></RoleGuard>} />
        <Route path="geotagging" element={<RoleGuard><GeotaggingPage /></RoleGuard>} />
        <Route path="web20-profiles" element={<RoleGuard><Web20ProfilesPage /></RoleGuard>} />
        <Route path="microblog" element={<RoleGuard><MicroblogPage /></RoleGuard>} />
        <Route path="sitemap" element={<RoleGuard><SitemapPage /></RoleGuard>} />
        <Route path="robots-txt" element={<RoleGuard><RobotsTxtPage /></RoleGuard>} />
        <Route path="site-audit" element={<RoleGuard><SiteAuditPage /></RoleGuard>} />
        <Route path="core-web-vitals" element={<RoleGuard><CoreWebVitalsPage /></RoleGuard>} />
        <Route path="image-sharing" element={<RoleGuard><ImageSharingPage /></RoleGuard>} />
        <Route path="infographic-distribution" element={<RoleGuard><InfographicDistributionPage /></RoleGuard>} />
        <Route path="pinterest" element={<RoleGuard><PinterestPage /></RoleGuard>} />
        <Route path="bing-places" element={<RoleGuard><BingPlacesPage /></RoleGuard>} />
        <Route path="classified-submissions" element={<RoleGuard><ClassifiedSubmissionsPage /></RoleGuard>} />
        <Route path="penalty-check" element={<RoleGuard><PenaltyCheckPage /></RoleGuard>} />
        <Route path="social-bookmarking" element={<RoleGuard><SocialBookmarkingPage /></RoleGuard>} />
        <Route path="syndication" element={<RoleGuard><SyndicationPage /></RoleGuard>} />
        <Route path="indexation" element={<RoleGuard><IndexationPage /></RoleGuard>} />
        <Route path="serp-ranking" element={<RoleGuard><SerpRankingPage /></RoleGuard>} />
        <Route path="duplicate-content" element={<RoleGuard><DuplicateContentPage /></RoleGuard>} />
        <Route path="package-usage" element={<RoleGuard><PackageUsagePage /></RoleGuard>} />
        <Route path="agency" element={<RoleGuard><AgencyDashboardPage /></RoleGuard>} />
        <Route path="admin/users" element={<RoleGuard><AdminUsersPage /></RoleGuard>} />
        <Route path="admin/packages" element={<RoleGuard><AdminPackagesPage /></RoleGuard>} />
        <Route path="admin/llm-usage" element={<RoleGuard><AdminLlmUsagePage /></RoleGuard>} />
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <TaskStateProvider>
            <ErrorBoundary>
              <AppRouter />
            </ErrorBoundary>
            <Toaster position="top-right" richColors />
          </TaskStateProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
